/**
 * hourly-due-rooms.ts — B7, reine Ableitung der "stündlichen Mess-Fälligkeit"
 * bei ASR-Stufe Kritisch (> 35 °C gemessen).
 *
 * Komplett FE-only: GET /v1/dashboard/status liefert pro Raum bereits
 * `last_entry.{event_at, confirmed_level}` — "fällig diese Stunde" ist daraus
 * rein client-seitig ableitbar. KEINE autoritative Compliance-Messung, fließt
 * NICHT in den versiegelten Record (advisory, konsistent mit Forecast-Karte B5).
 *
 * Red-Line: das hier ist eine ARBEITSLISTE ("noch offen diese Stunde: A, B"),
 * KEIN Erfüllungs-Score. Es gibt bewusst keine "x/y erledigt"-Zahl.
 *
 * TZ-Korrektheit: das Backend-ISO (`event_at`) trägt einen absoluten Instant.
 * "Heute" und "diese Stunde" sind aber lokale Begriffe — wir rechnen EXPLIZIT
 * in Europe/Berlin (nicht Browser-Locale: das Gerät der Leitung kann auf einer
 * anderen Zeitzone stehen, der Mess-Tag richtet sich nach der Einrichtung).
 */

/**
 * Minimaler Raum-Teiltyp für die Fälligkeits-Ableitung. Bewusst schlank
 * gehalten (nur was gebraucht wird) — strukturell kompatibel mit dem vollen
 * Room-Typ aus dashboard-route.tsx (extra-felder sind erlaubt), damit kein
 * Zirkular-Import nötig ist.
 */
export interface RoomForDue {
  room_id: string;
  name: string;
  last_entry?: {
    event_at: string;
    confirmed_level: "normal" | "soll" | "muss" | "kritisch";
  };
}

/** Ein fälliger/gemessener Kritisch-Raum — minimal für Link + Anzeige. */
export interface DueRoom {
  room_id: string;
  name: string;
}

export interface HourlyDueResult {
  /** Kritisch heute, aber zuletzt in einer FRÜHEREN Stunde gemessen. */
  openThisHour: DueRoom[];
  /** Kritisch heute UND bereits in DIESER Stunde gemessen. */
  doneThisHour: DueRoom[];
  /** true ⇔ ≥1 Raum steht heute auf Kritisch (Karte existiert nur dann). */
  anyKritischToday: boolean;
}

/**
 * Berlin-lokale Datums-/Stunden-Bestandteile eines absoluten Instants.
 * `hourCycle: "h23"` garantiert 00–23 (statt "24" bei Mitternacht, das manche
 * Intl-Engines bei hour12:false liefern). Liest die parts per type, daher ist
 * die Locale-Reihenfolge irrelevant — en-CA nur aus Konvention (ISO-ähnlich).
 */
function berlinParts(
  d: Date,
): { year: string; month: string; day: string; hour: string } | null {
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    hourCycle: "h23",
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((p) => p.type === type)?.value ?? "";
  const year = get("year");
  const month = get("month");
  const day = get("day");
  const hour = get("hour");
  if (!year || !month || !day || !hour) return null;
  return { year, month, day, hour };
}

/** Berlin-Tagesschlüssel `YYYY-MM-DD` (null bei unparsebarem Datum). */
export function berlinDateKey(d: Date): string | null {
  const p = berlinParts(d);
  return p ? `${p.year}-${p.month}-${p.day}` : null;
}

/** Berlin-Stundenschlüssel `YYYY-MM-DD-HH` (null bei unparsebarem Datum). */
export function berlinHourKey(d: Date): string | null {
  const p = berlinParts(d);
  return p ? `${p.year}-${p.month}-${p.day}-${p.hour}` : null;
}

/**
 * computeHourlyDueRooms — teilt die Räume in "diese Stunde noch offen" vs.
 * "diese Stunde bereits gemessen" auf, beschränkt auf HEUTE-Kritisch.
 *
 * Pro Raum qualifiziert nur, wenn:
 *   1. ein `last_entry` existiert,
 *   2. dessen `confirmed_level === "kritisch"` (neuester Eintrag = aktueller
 *      Zustand → ein auf "muss" abgekühlter Raum fällt automatisch raus), und
 *   3. der Eintrag HEUTE (Europe/Berlin) liegt — verhindert stale Gestern-
 *      Nudges. Die "nie heute gemessen"-Lücke ist B12, nicht B7.
 *
 * `now` ist injiziert (statt intern `new Date()`) → voll unit-testbar.
 */
export function computeHourlyDueRooms(
  rooms: readonly RoomForDue[],
  now: Date,
): HourlyDueResult {
  const openThisHour: DueRoom[] = [];
  const doneThisHour: DueRoom[] = [];

  const nowDateKey = berlinDateKey(now);
  const nowHourKey = berlinHourKey(now);
  if (nowDateKey === null || nowHourKey === null) {
    // `now` unbrauchbar (sollte nie passieren) → keine Karte, kein Nudge.
    return { openThisHour, doneThisHour, anyKritischToday: false };
  }

  for (const room of rooms) {
    const last = room.last_entry;
    if (!last) continue;
    if (last.confirmed_level !== "kritisch") continue;

    const eventDate = new Date(last.event_at);
    const eventDateKey = berlinDateKey(eventDate);
    if (eventDateKey === null) continue; // unparsebarer timestamp → ignorieren
    if (eventDateKey !== nowDateKey) continue; // nicht heute (Berlin) → ignorieren

    const dueRoom: DueRoom = { room_id: room.room_id, name: room.name };
    if (berlinHourKey(eventDate) === nowHourKey) {
      doneThisHour.push(dueRoom);
    } else {
      openThisHour.push(dueRoom);
    }
  }

  return {
    openThisHour,
    doneThisHour,
    anyKritischToday: openThisHour.length > 0 || doneThisHour.length > 0,
  };
}
